/**
 * The plan as a file the dietitian can keep — what the row menu's Download
 * hands over.
 *
 * The document itself is the backend's: `GET /api/v1/supplement-plans/{id}/pdf/`
 * renders it, headed with the dietitian's own brand, and this module is only
 * what happens to the bytes once they arrive. That is deliberate — what the
 * client is emailed and what the dietitian downloads are then the same
 * document, rather than two renderings that drift apart. The same arrangement
 * `planDocument` makes for a meal plan.
 */

import type { SupplementPlanSummary } from "./types/supplements.types";

/**
 * `sup-w01-00004-dia-rode.pdf` — the plan's own name, so two never collide.
 *
 * The whole name rather than its tail: "00004" alone says nothing about what
 * the file is once it is sitting in a downloads folder beside a meal plan and
 * an exercise plan.
 */
export const supplementPlanFileName = (
  plan: SupplementPlanSummary
): string => {
  const slug = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  const name = slug(plan.name) || "supplement-plan";
  const client = slug(plan.clientName);

  return `${name}${client ? `-${client}` : ""}.pdf`;
};

/**
 * Hands the file to the browser. The object URL is released straight after the
 * click — the download has already been handed off by then, and holding it
 * would keep the whole document in memory for the life of the tab.
 */
export const saveSupplementPlanPdf = (
  plan: SupplementPlanSummary,
  pdf: Blob
): void => {
  const url = URL.createObjectURL(pdf);

  const link = document.createElement("a");
  link.href = url;
  link.download = supplementPlanFileName(plan);
  document.body.appendChild(link);
  link.click();
  link.remove();

  URL.revokeObjectURL(url);
};
